import type { ISize } from '@/version-types';
import { SVG_NS } from './constants';

type SVGNamespacedAttributes = Record<string, Record<string, string | number>>;

/**
 * 创建 SVG 命名空间节点并批量设置属性。
 *
 * @param tagName - 需要创建的 SVG 标签名。
 * @param attributes - 需要写入节点的属性，默认为空对象。
 * @param namespacedAttributes - 以 namespace URI 为键、qualified name 为内层键的属性，默认为空对象。
 * @returns 创建完成的 SVG 节点。
 */
export const createSVGElement = <K extends keyof SVGElementTagNameMap>(
    tagName: K,
    attributes: Record<string, string | number> = {},
    namespacedAttributes: SVGNamespacedAttributes = {},
): SVGElementTagNameMap[K] => {
    const element = document.createElementNS(SVG_NS, tagName);

    Object.entries(attributes).forEach(([name, value]) => {
        element.setAttribute(name, String(value));
    });

    Object.entries(namespacedAttributes).forEach(([namespace, attributes]) => {
        Object.entries(attributes).forEach(([name, value]) => {
            element.setAttributeNS(namespace, name, String(value));
        });
    });

    return element;
};

/**
 * 读取 SVG 根节点声明的宽高。
 *
 * @param svg - 需要读取尺寸的 SVG 根节点。
 * @returns 转换为数值后的 SVG 尺寸。
 */
export const readSVGSize = (svg: SVGSVGElement): ISize => ({
    width: Number(svg.getAttribute('width')),
    height: Number(svg.getAttribute('height')),
});
